import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { utils } from "../../Helpers";
import {
  BanknoteArrowDown,
  CircleCheckBig,
  CircleX,
  Eye,
  Info,
  LifeBuoy,
  LogOut,
  MessageCircleMore,
  MessageSquareDiff,
  Rocket,
  SquarePen,
  StickyNote,
} from "lucide-react";
import { offerService } from "../../Services";
import {
  Tooltip,
  TableCell,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DescriptionIcon from "@mui/icons-material/Description";
import { decodeFromBase64 } from "../../Components/Constants/AppConstant";
import BrandOfferDetails from "../Offer/BrandOfferDetails";

export default function OfferItemByCampaignId(props) {
  const isReuqstedBy = props?.user?.data?.id === props.cancel_requested_by;
  const [modalApplicationData, setModalApplicationData] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [applicaitonData] = useState(props);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const navigate = useNavigate();

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  function capitalizeFirstLetter(val) {
    return String(val)?.charAt(0)?.toUpperCase() + String(val)?.slice(1);
  }

  function goToReview() {
    navigate("/brand/review");
  }

  const startchat = (id, userId, username) => {
    props.onStartChat({ offerId: id, userId: { value: username, id: userId } });
    navigate("/brand/chat");
    handleMenuClose();
  };

  const getPlatformIcon = (platformCode) => {
    const id = platformCode?.toString();

    switch (id) {
      case "IG":
        return (
          <img
            src="/images/instagram.svg"
            alt="Instagram"
            width="20"
            height="20"
          />
        );
      case "TT":
        return (
          <img
            src="/images/tiktok-logo.svg"
            alt="TikTok"
            width="20"
            height="20"
          />
        );
      case "FB":
        return (
          <img
            src="/images/facebook.svg"
            alt="Facebook"
            width="20"
            height="20"
          />
        );
      case "YT":
        return (
          <img src="/images/youtube.svg" alt="YouTube" width="20" height="20" />
        );
      default:
        return (
          <span className="text-xs text-gray-400">{platformCode || "NA"}</span>
        );
    }
  };

  const handleApplicationDetails = (id) => {
    setModalApplicationData("detail");
    setApplicationId(id);
  };

  const statusRender = (status) => {
    const baseStyle =
      "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap";

    switch (status) {
      case "pending":
        return (
          <span className={`${baseStyle} bg-gray-100 text-gray-700`}>
            {capitalizeFirstLetter(status)}
          </span>
        );

      case "accepted":
        return (
          <span className={`${baseStyle} bg-blue-100 text-blue-700`}>
            {capitalizeFirstLetter(status)}
          </span>
        );

      case "rejected":
        return (
          <span className={`${baseStyle} bg-red-100 text-red-700`}>
            {capitalizeFirstLetter(status)}
          </span>
        );

      case "complete":
        return (
          <span className={`${baseStyle} bg-green-100 text-green-700`}>
            Complete
          </span>
        );

      case "complete_pending":
        return (
          <Tooltip title="Waiting for the brand's acceptance" arrow>
            <span className={`${baseStyle} bg-yellow-100 text-yellow-700`}>
              <span>Complete</span>
              <Info size={14} />
            </span>
          </Tooltip>
        );
      case "cancel_requested":
        return (
          <span
            className={`${baseStyle} ${
              isReuqstedBy
                ? "bg-blue-100 text-blue-600"
                : "bg-yellow-100 text-yellow-600"
            }`}
          >
            <span>{isReuqstedBy ? "Cancel Requested" : "Cancel Received"}</span>
          </span>
        );
      case "modification_requested":
        return (
          <span className={`${baseStyle} bg-blue-100 text-blue-600`}>
            <span>Modification Requested</span>
          </span>
        );
      case "escalated":
        return (
          <span className={`${baseStyle} bg-red-100 text-red-600`}>
            <span>Escalated</span>
          </span>
        );
      case "withdrawn":
        return (
          <span className={`${baseStyle} bg-green-100 text-green-600`}>
            <span>Withdrawn</span>
          </span>
        );

      default:
        return (
          <span className={`${baseStyle} bg-gray-100 text-gray-600`}>
            {capitalizeFirstLetter(status)}
          </span>
        );
    }
  };

  const handleOfferAction = async (action, closeModal = false) => {
    try {
      await offerService.setOfferActions(props.id, action);
      props.reFetchData();
    } catch (err) {
      let errorMessage = `Failed to ${action} offer.`;
      if (err.response?.data) {
        const decodedError = decodeFromBase64(err.response.data);
        errorMessage = decodedError.message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      console.error(errorMessage);
    }
    handleMenuClose();
  };

  const handleAcceptOffer = () => handleOfferAction("withdraw", true);
  const handleSignOff = () => handleOfferAction("approve", true);
  const handleRequestedModification = () => handleOfferAction("modify", true);
  const handleCancelRequest = () => handleOfferAction("cancel/approve", true);
  const handleEscalateToSupport = () => handleOfferAction("escalate", true);
  const handleCancelCollab = () => handleOfferAction("cancel", true);

  const getAvailableActions = () => {
    const actions = [
      {
        key: "chat",
        label: "Chat",
        icon: MessageCircleMore,
        color: "#10B981",
        hoverColor: "#dcfce7",
        onClick: () => startchat(props.id, props.to_user_id, props.username),
      },
      {
        key: "view",
        label: "View",
        icon: Eye,
        color: "#10B981",
        hoverColor: "#dcfce7",
        onClick: () => handleApplicationDetails(props.id),
      },
    ];

    if (props.status === "complete") {
      actions.push({
        key: "review",
        label: "Add Review",
        icon: MessageSquareDiff,
        color: "#3b82f6",
        hoverColor: "#dbeafe",
        onClick: () => {
          props.setOfferSelectedForReview(props.id);
          goToReview();
          handleMenuClose();
        },
      });
    }

    if (props.status === "pending") {
      actions.push({
        key: "withdraw",
        label: "Withdraw Offer",
        icon: BanknoteArrowDown,
        color: "#16a34a",
        hoverColor: "#dcfce7",
        onClick: handleAcceptOffer,
      });
    }

    if (props.status === "complete_pending") {
      actions.push(
        {
          key: "signoff",
          label: "Sign Off",
          icon: LogOut,
          color: "#16a34a",
          hoverColor: "#dcfce7",
          onClick: handleSignOff,
        },
        {
          key: "modify",
          label: "Request Modification",
          icon: SquarePen,
          color: "#dc2626",
          hoverColor: "#fecaca",
          onClick: handleRequestedModification,
        }
      );
    }

    if (props.status === "cancel_requested" && !isReuqstedBy) {
      actions.push(
        {
          key: "agree_cancel",
          label: "Agree Cancellation",
          icon: CircleCheckBig,
          color: "#16a34a",
          hoverColor: "#dcfce7",
          onClick: handleCancelRequest,
        },
        {
          key: "escalate",
          label: "Escalate to Support",
          icon: LifeBuoy,
          color: "#eab308",
          hoverColor: "#fef08a",
          onClick: handleEscalateToSupport,
        }
      );
    }

    if (
      props.status === "modification_requested" ||
      props.status === "accepted" ||
      props.status === "complete_pending"
    ) {
      actions.push({
        key: "cancel",
        label: "Cancel Collab",
        icon: CircleX,
        color: "#dc2626",
        hoverColor: "#fecaca",
        onClick: handleCancelCollab,
      });
    }
    if (props.has_report) {
      actions.push({
        key: "viewReports",
        label: "View Reports",
        icon: DescriptionIcon,
        color: "#9CA3AF",
        hoverColor: "#cffafe",
      });
    }

    return actions;
  };

  const renderCellWithTooltip = (content, maxLength = 30) => {
    if (!content) return null;

    const truncated =
      content.length > maxLength
        ? `${content.substring(0, maxLength)}...`
        : content;

    return (
      <Tooltip title={content} arrow placement="top">
        <span className="truncate">{truncated}</span>
      </Tooltip>
    );
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          "& td": {
            py: "4px",
            px: 2.5,
            borderBottom: "1px solid #F3F4F6",
            fontSize: "14px",
          },
          "&:hover": {
            backgroundColor: "#FAFAFA !important",
          },
          "&:nth-of-type(even)": {
            backgroundColor: "rgba(248, 250, 252, 0.5)",
          },
          cursor: "pointer",
        }}
        onClick={() => handleApplicationDetails(props.id)}
      >
        <TableCell
          sx={{
            width: { xs: "40px", lg: "40px" },
            minWidth: "40px",
            paddingLeft: "12px",
          }}
        >
          <Tooltip
            title={
              props.ds_campaign_application_id
                ? "Campaign Application"
                : "Direct Offer"
            }
            arrow
          >
            <span>
              {props.ds_campaign_application_id ? (
                <StickyNote color="#15803d" size={20} />
              ) : (
                <Rocket color="#15803d" size={20} />
              )}
            </span>
          </Tooltip>
        </TableCell>

        <TableCell
          sx={{
            color: "#64748B",
            fontSize: "14px",
            fontWeight: 400,
            width: { xs: "120px", lg: "auto" },
            minWidth: "100px",
          }}
        >
          {utils.formatdate(props?.created_at)}
        </TableCell>

        <TableCell
          sx={{
            color: "#1F2937",
            fontWeight: 500,
            fontSize: "14px",
            width: { xs: "180px", lg: "auto" },
            minWidth: "150px",
          }}
        >
          <div className="flex items-center gap-2">
            <img
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                flexShrink: 0,
              }}
              src={
                props.to_profile_img_url && props.to_profile_img_url !== ""
                  ? props.to_profile_img_url
                  : `${process.env.PUBLIC_URL}/images/placeholder/avatar.png`
              }
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = `${process.env.PUBLIC_URL}/images/placeholder/avatar.png`;
              }}
              alt="avatar"
            />
            <span className="truncate">{props?.to_displayname}</span>
          </div>
        </TableCell>

        <TableCell
          sx={{
            color: "#475569",
            fontSize: "14px",
            fontWeight: 400,
            width: { xs: "250px", lg: "auto" },
            minWidth: "200px",
          }}
        >
          <div className="flex gap-2 items-start">
            {getPlatformIcon(props?.social_media_code)}
            <span className="text-sm text-gray-800 leading-snug line-clamp-2">
              {renderCellWithTooltip(props.description)}
            </span>
          </div>
        </TableCell>

        <TableCell
          sx={{
            textAlign: "center",
            color: "#1F2937",
            fontSize: "14px",
            fontWeight: 600,
            width: { xs: "120px", lg: "auto" },
            minWidth: "100px",
          }}
        >
          ${props.price}
        </TableCell>

        <TableCell
          sx={{
            textAlign: "center",
            width: { xs: "140px", lg: "auto" },
            minWidth: "120px",
          }}
        >
          {statusRender(props.status)}
        </TableCell>

        <TableCell
          sx={{
            textAlign: "center",
            width: { xs: "180px", lg: "auto" },
            minWidth: "150px",
          }}
        >
          <div className="">
            {/* <button
              className="px-4 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleApplicationDetails(props.id);
              }}
            >
              View
            </button> */}

            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{
                color: "#6B7280",
                "&:hover": {
                  color: "#10B981",
                  backgroundColor: "rgba(16, 185, 129, 0.1)",
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "white",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                  padding: "4px",
                  marginTop: "18px",
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              {getAvailableActions().map((action, index, actions) => (
                <MenuItem
                  key={action.key}
                  onClick={(event) => {
                    if (action.key === "viewReports") {
                      navigate(
                        `/brand/reports/insights/${props.id}?source=campaignOffer`
                      );
                    } else {
                      event.stopPropagation();
                      action.onClick();
                    }
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    margin: "2px 0",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#334155",
                    transition: "all 0.2s ease",
                    borderBottom:
                      index === actions.length - 1 ? "0" : "1px solid #f3f4f6",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: action.hoverColor,
                    },
                  }}
                >
                  <ListItemIcon>
                    <action.icon sx={{ color: action.color }} size={18} />
                  </ListItemIcon>
                  <ListItemText
                    primary={action.label}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </div>
        </TableCell>
      </TableRow>

      <BrandOfferDetails
        open={modalApplicationData === "detail"}
        onClose={() => setModalApplicationData(null)}
        applicationId={applicationId}
        applicaitonData={applicaitonData}
      />
    </>
  );
}
